/**
 * Intune / device posture checks (C9) — ported from x365 modules/intune.js into
 * pure functions emitting ControlResult[] for the evidence engine.
 *
 * Scope-scoped: covers the device/policy controls reachable with C9's least-privilege
 * device reads. User-coverage and enrollment (which need directory reads) are left
 * untested here and evaluated by the Identity module's consent.
 */
import type { AffectedObject, ControlResult } from '@/compliance/evidence/types';
import {
  INTUNE_ENDPOINTS,
  getCompliancePolicies,
  getConfigProfiles,
  getEndpointSecurityPolicies,
  getManagedDevices,
  type CompliancePolicy,
  type ConfigProfile,
  type EndpointSecurityIntent,
  type ManagedDevice,
  type PolicyAssignment,
} from '@/graph/modules/intune';

const STALE_DAYS = 30;

export interface IntuneData {
  compliancePolicies: CompliancePolicy[];
  configProfiles: ConfigProfile[];
  devices: ManagedDevice[];
  secPolicies: EndpointSecurityIntent[];
}

export async function collectIntuneData(): Promise<IntuneData> {
  const [compliancePolicies, configProfiles, devices, secPolicies] = await Promise.all([
    getCompliancePolicies().catch(() => [] as CompliancePolicy[]),
    getConfigProfiles().catch(() => [] as ConfigProfile[]),
    getManagedDevices().catch(() => [] as ManagedDevice[]),
    getEndpointSecurityPolicies().catch(() => [] as EndpointSecurityIntent[]),
  ]);
  return { compliancePolicies, configProfiles, devices, secPolicies };
}

function groupIds(assignments: PolicyAssignment[] = []): string[] {
  return assignments.map((a) => a.target?.groupId).filter((g): g is string => !!g);
}

interface Setting {
  key: string;
  value: string;
}
function extractSettings(p: ConfigProfile): Setting[] {
  const out: Setting[] = [];
  for (const s of p.omaSettings ?? []) {
    if (s.omaUri) out.push({ key: s.omaUri, value: String(s.value ?? s.secretValue ?? '') });
  }
  if (p.storageRequireEncryption != null) out.push({ key: 'storageRequireEncryption', value: String(p.storageRequireEncryption) });
  if (p.bitLockerEnabled != null) out.push({ key: 'bitLockerEnabled', value: String(p.bitLockerEnabled) });
  return out;
}

function daysSince(iso?: string | null): number {
  if (!iso) return 9999;
  return Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
}

export function evaluateIntune(data: IntuneData): ControlResult[] {
  const { compliancePolicies, configProfiles, devices, secPolicies } = data;
  const results: ControlResult[] = [];

  // Stale devices (> 30 days since sync).
  const stale = devices.filter((d) => daysSince(d.lastSyncDateTime) > STALE_DAYS);
  results.push({
    controlId: 'intune_device_stale',
    status: stale.length > 0 ? 'fail' : 'pass',
    affected: stale.map((d) => ({
      type: 'device',
      id: d.id,
      label: d.deviceName ?? d.id,
      detail: `${daysSince(d.lastSyncDateTime)}d since sync · ${d.operatingSystem ?? 'unknown OS'}`,
    })),
    endpoints: [INTUNE_ENDPOINTS.managedDevices],
  });

  // Non-compliant devices.
  const nonCompliant = devices.filter((d) => d.complianceState === 'noncompliant');
  results.push({
    controlId: 'intune_device_non_compliant',
    status: nonCompliant.length > 0 ? 'fail' : 'pass',
    affected: nonCompliant.map((d) => ({
      type: 'device',
      id: d.id,
      label: d.deviceName ?? d.id,
      detail: `${d.operatingSystem ?? 'unknown OS'} · ${d.userDisplayName ?? 'no user'}`,
    })),
    endpoints: [INTUNE_ENDPOINTS.managedDevices],
  });

  // Configuration-profile conflicts (same setting + shared group, different value = high; same = medium).
  const settingMap = new Map<string, { name: string; value: string; groups: string[] }[]>();
  for (const p of configProfiles) {
    const groups = groupIds(p.assignments);
    if (groups.length === 0) continue;
    for (const s of extractSettings(p)) {
      let bucket = settingMap.get(s.key);
      if (!bucket) {
        bucket = [];
        settingMap.set(s.key, bucket);
      }
      bucket.push({ name: p.displayName ?? p.id, value: s.value, groups });
    }
  }
  const highConflicts: AffectedObject[] = [];
  const medConflicts: AffectedObject[] = [];
  for (const [key, entries] of settingMap) {
    for (let i = 0; i < entries.length; i++) {
      for (let j = i + 1; j < entries.length; j++) {
        const a = entries[i];
        const b = entries[j];
        if (!a || !b) continue;
        if (!a.groups.some((g) => b.groups.includes(g))) continue;
        const obj: AffectedObject = {
          type: 'policy',
          id: `${key}:${i}:${j}`,
          label: `${a.name} ↔ ${b.name}`,
          detail: `setting "${key}" (${a.value} vs ${b.value})`,
        };
        if (a.value !== b.value) highConflicts.push(obj);
        else medConflicts.push(obj);
      }
    }
  }
  results.push({
    controlId: 'intune_policy_conflict_high',
    status: highConflicts.length > 0 ? 'fail' : 'pass',
    affected: highConflicts,
    endpoints: [INTUNE_ENDPOINTS.configProfiles],
  });
  results.push({
    controlId: 'intune_policy_conflict_medium',
    status: medConflicts.length > 0 ? 'fail' : 'pass',
    affected: medConflicts,
    endpoints: [INTUNE_ENDPOINTS.configProfiles],
  });

  // Encryption policy present?
  const encryption = configProfiles.filter(
    (p) =>
      /encrypt|bitlocker|filevault/i.test(p.displayName ?? '') ||
      p.storageRequireEncryption === true ||
      p.bitLockerEnabled === true ||
      (p.omaSettings ?? []).some((s) => /encrypt|bitlocker|filevault/i.test(String(s.omaUri ?? ''))),
  );
  results.push({
    controlId: 'intune_no_encryption_policy',
    status: encryption.length > 0 ? 'pass' : 'fail',
    affected: encryption.length > 0 ? [] : [{ type: 'tenant', id: 'enc', label: 'No device encryption policy found' }],
    endpoints: [INTUNE_ENDPOINTS.configProfiles, INTUNE_ENDPOINTS.intents],
  });

  // Password/PIN policy present?
  const password = compliancePolicies.filter(
    (p) => p.passwordRequired === true || p.passwordMinimumLength != null || /password|pin/i.test(p.displayName ?? ''),
  );
  results.push({
    controlId: 'intune_no_password_policy',
    status: password.length > 0 ? 'pass' : 'fail',
    affected: password.length > 0 ? [] : [{ type: 'tenant', id: 'pwd', label: 'No device password/PIN policy found' }],
    endpoints: [INTUNE_ENDPOINTS.compliancePolicies],
  });

  // Antivirus / Defender policy present?
  const av =
    configProfiles.filter((p) => /defender|antivirus|endpoint.protection|malware/i.test(p.displayName ?? '')).length +
    secPolicies.filter((p) => /antivirus|endpoint.security|defender/i.test(p.displayName ?? '')).length;
  results.push({
    controlId: 'intune_no_antivirus_policy',
    status: av > 0 ? 'pass' : 'fail',
    affected: av > 0 ? [] : [{ type: 'tenant', id: 'av', label: 'No antivirus / Defender policy found' }],
    endpoints: [INTUNE_ENDPOINTS.configProfiles, INTUNE_ENDPOINTS.intents],
  });

  // Orphaned (unassigned) policies.
  const orphaned: AffectedObject[] = [
    ...compliancePolicies.filter((p) => (p.assignments ?? []).length === 0).map((p) => ({
      type: 'policy' as const,
      id: p.id,
      label: p.displayName ?? p.id,
      detail: 'compliance policy, no assignment',
    })),
    ...configProfiles.filter((p) => (p.assignments ?? []).length === 0).map((p) => ({
      type: 'policy' as const,
      id: p.id,
      label: p.displayName ?? p.id,
      detail: 'config profile, no assignment',
    })),
  ];
  results.push({
    controlId: 'intune_unassigned_policy',
    status: orphaned.length > 0 ? 'fail' : 'pass',
    affected: orphaned,
    endpoints: [INTUNE_ENDPOINTS.compliancePolicies, INTUNE_ENDPOINTS.configProfiles],
  });

  return results;
}

export async function auditIntune(): Promise<ControlResult[]> {
  return evaluateIntune(await collectIntuneData());
}
